import React, { useState } from "react";
import { EuiButton, EuiIcon, EuiTextColor } from "@elastic/eui";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { useIntl } from "react-intl";
import { extractResourceAttributes } from "../../../../utils/jsonApiUtils";

import { dateToFormattedTime } from "../../../../utils/dateAndTime";
import DownRightIcon from "../../../shared/icons/DownRightArrowIcon";
import RightArrowIcon from "../../../shared/icons/RightArrowIcon";
import { AddDropOffsModal } from "../shared/AddDropOffsModal";
import { updateEntity } from "../../../shared/entity/slice";
import DropOffDetailsOverlay from "../shared/DropOffDetailsOverlay";

import * as S from "../../style";
import { getCurrentPointId } from "../../utils";
import { isSuperAdmin } from "../../../auth/slice";
import { isTerminationState } from "../../constants/tripStates";

const dropoffShowMoreStep = {
    locationAttributes: { data: { attributes: { address: " ... ", name: " ... " } } },
};

function TripPointsCell({ trip, tripPoints, fetchTrips, tripType }) {
    const dispatch = useDispatch();
    const pickupPoint = tripPoints.length && tripPoints.find((p) => p.pointType === "pickup");
    const pickupPointLocation =
        (pickupPoint && extractResourceAttributes(pickupPoint.locationAttributes)) || {};

    const dropOffPoints = tripPoints && tripPoints.filter((p) => p.pointType === "dropoff");
    const hidePoints = dropOffPoints.length >= 3;

    const isCurrentUserSuperAdmin = useSelector(isSuperAdmin());
    const canAppend = isCurrentUserSuperAdmin || !isTerminationState(trip.status);
    const canModify = pickupPoint?.canModify;

    const [showModal, setShowModal] = useState(false);
    const [showPointsDetails, setShowPointsDetails] = useState(false);
    const intl = useIntl();

    const renderDropOffs = () => {
        const currentPointId = getCurrentPointId(
            dropOffPoints,
            trip.currentTripPointId,
            trip.status
        );
        let isCurrentTripFound = +pickupPoint?.id === currentPointId;

        if (hidePoints) {
            dropOffPoints[1] = { ...dropOffPoints[1], ..._.cloneDeep(dropoffShowMoreStep) };
        }

        return dropOffPoints.map((dropoff, i) => {
            const locationAttributes = extractResourceAttributes(dropoff.locationAttributes);
            const dropoffName =
                locationAttributes.nameAr ||
                locationAttributes.name ||
                locationAttributes.address ||
                dropoff.cityName;

            const dropOffRender = (
                <span
                    onClick={() => setShowPointsDetails(true)}
                    key={dropoff.id}
                    className="points-cell__dropoff"
                >
                    {i !== 0 && (
                        <span className="points-cell__dropoff-arrow">
                            <RightArrowIcon />
                        </span>
                    )}
                    {!isCurrentTripFound && currentPointId !== null && (
                        <span className="points-cell__point-completed-container">
                            <EuiIcon
                                type="checkInCircleFilled"
                                color="success"
                            />
                        </span>
                    )}
                    <span>{dropoffName}</span>
                </span>
            );

            if (!isCurrentTripFound && +dropoff.id === currentPointId) {
                isCurrentTripFound = true;
            }

            return dropOffRender;
        });
    };

    const editTrip = async (dropOffs) => {
        const tripEntityAttributes = { tripPoints: [...dropOffs] };

        await dispatch(updateEntity("trips", trip.id, tripEntityAttributes));
        fetchTrips();
        setShowModal(false);
    };
    
    return (
        <S.TripPoinstCellWrapper hidePoints={hidePoints}>
            <div>
                <span>
                    <b className="points-cell__pickup-address">
                        {pickupPointLocation.nameAr ||
                            pickupPointLocation.name ||
                            pickupPointLocation.address ||
                            "N/A"}
                    </b>

                    <EuiTextColor
                        component="span"
                        color="subdued"
                        size="xs"
                        className="points-cell__pickup-date"
                    >
                        <small>
                            {pickupPointLocation.createdAt
                                ? dateToFormattedTime(pickupPointLocation.createdAt)
                                : ""}
                        </small>
                    </EuiTextColor>
                </span>
            </div>

            <div>
                <span className="points-cell__dropoffs">
                    <span className="points-cell__pickup-arrow">
                        <DownRightIcon />
                    </span>
                    <span className="points-cell__dropoffs-content">
                        {dropOffPoints.length ? (
                            renderDropOffs()
                        ) : (
                            <EuiButton
                                size="s"
                                onClick={() => setShowModal(true)}
                            >
                                {intl.formatMessage({
                                    id: "trip-actions.table-cell-enter-dropoffs-details",
                                })}
                            </EuiButton>
                        )}
                    </span>
                </span>
            </div>
            {showModal && (
                <AddDropOffsModal
                    tripId={trip.id}
                    closeModal={() => setShowModal(false)}
                    saveDropoffs={editTrip}
                    canAppend={canAppend}
                    canModify={canModify}
                    tripType={tripType}
                    pickupPoint={pickupPoint}
                    organizationId={trip.organizationId}
                />
            )}

            {showPointsDetails && (
                <DropOffDetailsOverlay
                    onHide={() => setShowPointsDetails(false)}
                    tripPoints={tripPoints}
                    currentTripPointId={trip.currentTripPointId}
                    tripStatus={trip.status}
                />
            )}
        </S.TripPoinstCellWrapper>
    );
}

export default TripPointsCell;
